import * as React from 'react';

import PropTypes from 'prop-types';
import _ from 'underscore';

import TableHeader from 'bundles/internal-ui/components/table/TableHeader';
import TableRow from 'bundles/internal-ui/components/table/TableRow';
import type { Action, Column } from 'bundles/internal-ui/components/table/types';

import 'css!./__styles__/Table';

// AI-Generated Documentation - Please Read with Caution

type Row = Record<string, any>;

type Props = {
  /** Array of data rows to display in the table */
  rows: Array<Row>;
  /** Column configurations defining the table structure */
  columns: Array<Column>;
  /** Column key to sort by initially */
  initialSortColumn?: string;
  /** Whether to sort in descending order initially */
  sortDescending?: boolean;
  /** Function to filter rows */
  filter?: () => boolean;
  /** Function to generate unique row identifiers */
  rowId?: (x0: Row) => string | number;
  /** Array of action buttons to display for each row */
  actions?: Array<Action>;
  /** When true, allows toggling between ascending and descending sort */
  isSortToggleEnabled?: boolean;
};

type State = {
  sortColumn?: string;
  sortDescending?: boolean;
};

/** A sortable and filterable data table component with action support. */
class Table extends React.Component<Props, State> {
  static propTypes = {
    rows: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
      })
    ).isRequired,
    initialSortColumn: PropTypes.string,
    sortDescending: PropTypes.bool,
    filter: PropTypes.func,
    rowId: PropTypes.func,
    actions: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string,
        tooltip: PropTypes.string,
        onClick: PropTypes.func,
      })
    ),
    isSortToggleEnabled: PropTypes.bool,
  };

  constructor(props: Props) {
    super(props);

    const { initialSortColumn, sortDescending } = props;
    this.state = {
      sortColumn: initialSortColumn,
      sortDescending,
    };
  }

  filteredData() {
    if (this.props.filter) {
      return this.props.rows.filter(this.props.filter);
    } else {
      return this.props.rows;
    }
  }

  sortedRows() {
    const { sortColumn, sortDescending } = this.state;
    if (sortColumn) {
      const results = _(this.filteredData()).sortBy(sortColumn);
      if (sortDescending) {
        return results.reverse();
      } else {
        return results;
      }
    } else {
      return this.filteredData();
    }
  }

  handleSortChange = (sortColumn: string, sortDescending: boolean) => {
    this.setState({
      sortColumn,
      sortDescending,
    });
  };

  render() {
    const { columns, actions, isSortToggleEnabled, rowId } = this.props;
    const { sortColumn, sortDescending } = this.state;
    return (
      <div>
        <table className="table table-responsive">
          <TableHeader
            columns={columns}
            actions={actions}
            onHeaderCellClick={this.handleSortChange}
            sortColumn={sortColumn}
            sortDescending={sortDescending}
            isSortToggleEnabled={isSortToggleEnabled}
          />
          <tbody>
            {this.sortedRows().map((row) => (
              <TableRow key={rowId ? rowId(row) : row.id} columns={columns} row={row} actions={actions} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

export default Table;
